import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bookingJson, sendJson } from "../../api";
import type { BookingPolicy, ClassroomPage } from "./types";

// The manage page's calls, the same ones v1 makes: the booking's classroom
// page and its policy (the booking id is what lets the customer in), a
// refund request on a session the school cancelled, and "Pay now", which
// alone needs the customer signed in (requireCustomerSession on
// POST …/pay-next-due: the session's email must be the buyer's).

export const classroomKey = (api: string, bookingId: string) => ["booking", "classroom", api, bookingId] as const;

export function useClassroom(api: string, bookingId: string) {
  return useQuery({
    queryKey: classroomKey(api, bookingId),
    queryFn: () => bookingJson<ClassroomPage>(`${api}/widget/pages/classroom/${encodeURIComponent(bookingId)}`),
    retry: (count, error) => count < 2 && !(error as { status?: number }).status,
  });
}

/** Fetched when the customer opens the policy, like v1's dialog. */
export function usePolicy(api: string, bookingId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["booking", "policy", api, bookingId],
    enabled,
    queryFn: async () =>
      (await bookingJson<{ policy: BookingPolicy }>(`${api}/widget/pages/bookings/${encodeURIComponent(bookingId)}/policy`)).policy,
    retry: false,
  });
}

/** Refunds the whole booking to the original payment method; the API
 * re-checks it may (409 when it no longer can, e.g. already refunded). */
export function useRequestRefund(api: string, bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sendJson<{ ok: string }>(`${api}/widget/pages/bookings/${encodeURIComponent(bookingId)}/request-refund`, "POST"),
    // Not awaited: the caller's own onSuccess must run while the banner that
    // asked is still mounted, before the refreshed booking takes it away.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: classroomKey(api, bookingId) });
    },
  });
}

/** Asks for the secure pay page of the next payment that is payable online
 * (the API picks the row) and answers its address. */
export function usePayNextDue(api: string, bookingId: string) {
  return useMutation({
    mutationFn: (token: string) =>
      sendJson<{ paymentUrl?: string; amountPence?: number }>(
        `${api}/widget/pages/bookings/${encodeURIComponent(bookingId)}/pay-next-due`,
        "POST",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      ),
  });
}
